// outlinerTree — the docked shell's outliner rows, derived from what is actually
// in the scene.
//
// Every row is REAL: counts come from the live THREE scene graph and the live
// graph-state modules, never from placeholder fixtures. A thing that isn't
// present contributes no row (the empty outliner is the no-character state), and
// a count we can't compute is omitted from the row rather than invented.
//
// Two kinds of row, distinguished by `adapterId`:
//   - adapter rows (Avatar / Environment / point + spot lights) — selecting one
//     arms the transform gizmo via sceneSelection, exactly as the old
//     OutlinerRail did.
//   - info rows (Armature / meshes / Face rig / Event graph / sun) — selecting
//     one only feeds the inspector. They exist because "what is this character
//     made of" is the outliner's job even where no gizmo applies.
//
// Pure function over injected inputs so `node --test` can drive it with a fake
// scene — no window, no THREE import (it reads structural booleans THREE
// already stamps on objects: isBone, isMesh, isSkinnedMesh).

/**
 * Full-viewport editor surface id → human label.
 *
 * Exported rather than inlined at the one call site below because the browser tab
 * title names the open surface too (src/lib/documentTitle.js). Two copies of these
 * three strings is two places to rename and one of them to forget.
 */
export const SURFACE_LABELS = Object.freeze({
  body: 'Body graph',
  face: 'Face graph',
  events: 'Event graph',
})

/**
 * Glyphs for the same three surfaces — tool rail, menu items and outliner rows.
 *
 * Every one is a TEXT-presentation character, and that is the whole constraint.
 * The Event Graph used to be '⚡' (U+26A1), which carries default EMOJI
 * presentation: browsers render it from a colour emoji font, so it ignored the
 * rail's `color` entirely — permanently orange, unaffected by hover or by the
 * active state that tints every other glyph. It read as a sticker dropped onto a
 * row of line icons because that is effectively what it was. '↯' is the same
 * meaning as a glyph the font actually draws.
 *
 * Face was '☺' (U+263A), a smiley — legible, but a cartoon among geometric marks.
 * '◎' reads as an iris and sits in the same family as the body hexagon.
 *
 * Kept deliberately plain and widely-covered (DejaVu, Segoe UI Symbol, Menlo): an
 * obscure APL or Miscellaneous-Technical glyph would be prettier on one machine
 * and a tofu box on the next.
 */
export const SURFACE_GLYPH = Object.freeze({
  body: '⬡',
  face: '◎',
  events: '↯',
})

/** Inspector type-badge colors ride the wire tokens, same convention as the
 *  launcher's pipeline badges: color encodes category, not theme. */
import { navmeshRowStatus, artifactRowStatus } from './navmeshStatus.js'
import { LIGHT_TYPES } from './lightTypes.js'
import { SCENE_WHITE } from './sceneColorDefaults.js'

export const TYPE_TOKEN = {
  CHARACTER: '--m-accent',
  SKELETON: '--m-wire-anim',
  MESH: '--m-wire-state',
  FACE: '--m-wire-state',
  GRAPH: '--m-wire-event',
  GSPLAT: '--m-wire-output',
  LIGHT: '--m-wire-output',
  NAVMESH: '--m-wire-anim',
  EMPTY: '--m-fg-3',
}

function countBones(scene) {
  let n = 0
  scene?.traverse?.(o => { if (o.isBone) n += 1 })
  return n
}

function collectMeshes(scene) {
  const out = []
  scene?.traverse?.(o => {
    if (!o.isMesh && !o.isSkinnedMesh) return
    const index = o.geometry?.index
    const pos = o.geometry?.attributes?.position
    const tris = index ? Math.floor(index.count / 3) : pos ? Math.floor(pos.count / 3) : null
    const morphs = o.morphTargetInfluences?.length ?? 0
    out.push({ name: o.name || '(unnamed mesh)', tris, morphs, visible: o.visible !== false })
  })
  return out
}

const fmt = n => (Number.isFinite(n) ? n.toLocaleString('en-US') : null)

// ── Lights as objects (ENG-1190) ─────────────────────────────────────────────
// One row per entry in liveTuneStore.lights, under a Lighting parent. Each row
// declares the light's properties as inspector controls, the way the Face rig
// and Navmesh rows do — a feature's properties live with the feature. The
// ranges and tooltips are the LiveTunePanel's, which these rows replace.

export const LIGHT_TYPE_META = Object.freeze({
  directional: 'SUN',
  spot: 'SPOT',
  point: 'POINT',
  hemisphere: 'HEMI',
})

const LIGHT_GLYPH = Object.freeze({
  directional: '✺',
  spot: '◤',
  point: '✦',
  hemisphere: '◠',
})

const LIGHT_TYPE_LABEL = Object.freeze({
  directional: 'Directional',
  spot: 'Spot',
  point: 'Point',
  hemisphere: 'Hemisphere',
})

const fix = (n) => (v) => Number(v).toFixed(n)
const spotAimed = (l) => Number.isFinite(l.aimX) && Number.isFinite(l.aimY) && Number.isFinite(l.aimZ)
const deg = (v) => `${Math.round(v)}°`
const MAP_SIZES = [[512, '512'], [1024, '1k'], [2048, '2k'], [4096, '4k']]

const SHADOW_CAST_TIP = 'A shadow-casting spotlight costs a whole extra render of the scene per frame '
  + 'plus two blur passes over its map. It is the single largest thing a light can switch on, and it does '
  + 'not get cheaper in a small window. ?spotshadows=0 turns it off for every spot at once.'
const SHADOW_MAP_TIP = 'Shadow-map resolution this light ASKS for, saved with the scene and shared with the team. Higher = finer gradient, less banding, and more memory: at 4k a light holds 128 MiB of render targets. Each viewer\'s own quality setting caps it for their machine.'

/** The map-size control, plus — when this viewer's quality setting is lowering
 *  it — a line saying what is actually on screen. A control reading 4k while the
 *  scene renders 512 is indistinguishable from a broken one (the same rule the
 *  disabled-with-a-reason controls follow). `shadowCap` is the cap in force,
 *  passed in from shadowQuality so this stays pure. */
function shadowMapControl(l, shadowCap, wrap = (c) => c) {
  const asked = l.shadowMapSize
  const capped = Number.isFinite(shadowCap) && Number.isFinite(asked) && shadowCap < asked
  return wrap({
    kind: 'choice', id: 'shadowMapSize', label: 'map', value: asked, options: MAP_SIZES,
    title: SHADOW_MAP_TIP,
    note: capped
      ? `Rendering at ${shadowCap} here — your quality setting caps it. The scene still asks for ${asked}.`
      : null,
  })
}
const SHADOW_BIAS_TIP = 'VSM light-bleed / contact-detachment tuning. Nudge if you see light leaking under the feet or the shadow floating off them.'

/** The controls one light row offers, by its type. `inert` disables the light's
 *  own knobs WITH the reason (a directional with no environment to cast in). */
function lightControls(l, { inert = null, shadowCap = null } = {}) {
  const on = inert == null
  const k = (c) => (on ? c : { ...c, enabled: false, title: inert })
  const name = { kind: 'text', id: 'name', label: 'name', value: l.name ?? '', title: 'How this light is listed in the outliner' }
  const remove = { kind: 'button', id: 'remove', label: 'Remove light', title: 'Delete this light from the scene' }
  switch (l.type) {
    case 'directional':
      return [
        name,
        { kind: 'group', label: 'Light' },
        k({ kind: 'slider', id: 'intensity', label: 'intensity', value: l.intensity, min: 0, max: 2, step: 0.05, format: fix(2),
          title: 'Kept low by default (0): the sun mainly CASTS — the IBL and key light do the lighting.' }),
        k({ kind: 'slider', id: 'temperature', label: 'temperature', value: l.temperature, min: 1000, max: 12000, step: 50, format: (v) => `${Math.round(v)}K`,
          title: 'Colour temperature in Kelvin. 6500 ≈ neutral; lower is warm, higher is cool.' }),
        { kind: 'group', label: 'Placement' },
        k({ kind: 'slider', id: 'elevation', label: 'elevation', value: l.elevation, min: 5, max: 89, step: 1, format: deg,
          title: 'Degrees above the horizon (90 = overhead). The live key light follows this too.' }),
        k({ kind: 'slider', id: 'azimuth', label: 'azimuth', value: l.azimuth, min: -180, max: 180, step: 1, format: deg,
          title: 'Degrees around the character, from +Z.' }),
        k({ kind: 'slider', id: 'distance', label: 'distance', value: l.distance, min: 1, max: 15, step: 0.1, format: (v) => `${fix(1)(v)} m`,
          title: 'How far from the head the sun sits. Changes the shadow frustum coverage, not the brightness.' }),
        { kind: 'group', label: 'Shadow' },
        shadowMapControl(l, shadowCap, k),
        k({ kind: 'slider', id: 'shadowCameraSize', label: 'frustum', value: l.shadowCameraSize, min: 1, max: 8, step: 0.1, format: (v) => `${fix(1)(v)} m`,
          title: 'Ortho frustum half-extent. Tighter around the avatar = more shadow texels per metre = crisper.' }),
        k({ kind: 'slider', id: 'shadowRadius', label: 'blur radius', value: l.shadowRadius, min: 0, max: 40, step: 1, format: fix(0) }),
        k({ kind: 'slider', id: 'shadowBlurSamples', label: 'blur samples', value: l.shadowBlurSamples, min: 1, max: 100, step: 1, format: fix(0) }),
        k({ kind: 'slider', id: 'shadowBias', label: 'bias', value: l.shadowBias, min: -0.005, max: 0.001, step: 0.0001, format: fix(4), title: SHADOW_BIAS_TIP }),
        remove,
      ]
    case 'spot':
      return [
        name,
        { kind: 'group', label: 'Light' },
        { kind: 'color', id: 'color', label: 'color', value: l.color },
        { kind: 'slider', id: 'intensity', label: 'intensity', value: l.intensity, min: 0, max: 300, step: 1, format: fix(0),
          title: 'Candela (physical units, decay 2).' },
        { kind: 'slider', id: 'range', label: 'range', value: l.range, min: 0, max: 50, step: 0.5, format: (v) => (v <= 0 ? '∞' : `${fix(1)(v)} m`),
          title: 'Maximum reach in metres; 0 = infinite.' },
        { kind: 'slider', id: 'angle', label: 'cone angle', value: l.angle, min: 5, max: 85, step: 1, format: deg, title: 'Cone half-angle.' },
        { kind: 'slider', id: 'penumbra', label: 'penumbra', value: l.penumbra, min: 0, max: 1, step: 0.02, format: fix(2), title: 'Soft edge: 0 hard → 1 fully soft.' },
        { kind: 'group', label: 'Placement' },
        { kind: 'slider', id: 'distance', label: 'distance', value: l.distance, min: 0, max: 15, step: 0.1, format: (v) => `${fix(1)(v)} m`,
          title: 'Out from the Y axis, along the rig\'s local +Z. Drag the gizmo instead if you prefer.' },
        { kind: 'slider', id: 'height', label: 'height', value: l.height, min: -5, max: 10, step: 0.1, format: (v) => `${fix(1)(v)} m` },
        { kind: 'slider', id: 'yaw', label: 'yaw', value: l.yaw, min: -180, max: 180, step: 1, format: deg, title: 'Rotation of the rig about the Y axis.' },
        // Two ways to aim: the rig's own (a point on the central axis, raised or
        // lowered here) or FREE — set by turning the rotate gizmo, which pins an
        // Euler on the entry. While free, the axis height is inert and says so;
        // "Aim at character" clears the free aim.
        { kind: 'slider', id: 'targetHeight', label: 'aim height', value: l.targetHeight, min: -2, max: 5, step: 0.05, format: (v) => `${fix(2)(v)} m`,
          enabled: !spotAimed(l),
          title: spotAimed(l)
            ? 'Aimed freely with the rotate gizmo — press "Aim at character" to aim at the axis again.'
            : 'The spot aims at a point on the central Y axis; this raises or lowers it, tilting the beam. Turn the rotate gizmo to aim it anywhere.' },
        ...(spotAimed(l) ? [{ kind: 'button', id: 'aim-at-axis', label: 'Aim at character', title: 'Drop the free aim and point at the central axis again (aim height above)' }] : []),
        { kind: 'toggle', id: 'gizmo', label: 'Always show cone', value: !!l.gizmo, enabled: true, title: 'Keep the cone drawn when the light is not selected (it always shows while selected)' },
        { kind: 'group', label: 'Shadow' },
        // CASTING AT ALL is the first control here because it is by far the
        // largest: a shadow-casting spotlight is a whole extra render of the
        // scene per frame plus two VSM blur passes, and the cost is
        // PIXEL-INDEPENDENT, so a small window does not make it cheaper. Every
        // control below it only shapes a cost this one decides to pay.
        { kind: 'toggle', id: 'castShadow', label: 'cast shadows', value: l.castShadow !== false, enabled: true,
          title: SHADOW_CAST_TIP },
        shadowMapControl(l, shadowCap, (c) => ({ ...c, enabled: l.castShadow !== false })),
        { kind: 'slider', id: 'shadowRadius', label: 'blur radius', value: l.shadowRadius, min: 0, max: 40, step: 1, format: fix(0), enabled: l.castShadow !== false },
        { kind: 'slider', id: 'shadowBlurSamples', label: 'blur samples', value: l.shadowBlurSamples, min: 1, max: 100, step: 1, format: fix(0), enabled: l.castShadow !== false },
        { kind: 'slider', id: 'shadowBias', label: 'bias', value: l.shadowBias, min: -0.005, max: 0.001, step: 0.0001, format: fix(4), title: SHADOW_BIAS_TIP, enabled: l.castShadow !== false },
        remove,
      ]
    case 'point':
      return [
        name,
        { kind: 'group', label: 'Light' },
        { kind: 'color', id: 'color', label: 'color', value: l.color },
        { kind: 'slider', id: 'intensity', label: 'intensity', value: l.intensity, min: 0, max: 12, step: 0.1, format: fix(1),
          title: 'Candela (decay 2), so intensity and distance are tied — halve the distance and it lands 4× brighter.' },
        { kind: 'group', label: 'Placement' },
        { kind: 'slider', id: 'targetHeight', label: 'aim height', value: l.targetHeight, min: 0, max: 2.5, step: 0.01, format: (v) => `${fix(2)(v)} m`,
          title: 'World Y the light orbits — set it to head height so elevation/azimuth read as angles about the face.' },
        { kind: 'slider', id: 'distance', label: 'distance', value: l.distance, min: 0.1, max: 4, step: 0.01, format: (v) => `${fix(2)(v)} m` },
        { kind: 'slider', id: 'elevation', label: 'elevation', value: l.elevation, min: -90, max: 90, step: 1, format: deg },
        { kind: 'slider', id: 'azimuth', label: 'azimuth', value: l.azimuth, min: -180, max: 180, step: 1, format: deg },
        {
          kind: 'button', id: 'place-at-camera', label: l.followCamera ? 'Drop it here' : 'Place at camera',
          title: l.followCamera
            ? 'Let go of the camera and leave the light where you are looking from'
            : 'Put the light on the view axis just in front of the camera and select it, so there\'s a gizmo to drag. Frame the subject first.',
        },
        { kind: 'toggle', id: 'followCamera', label: 'Ride camera (headlamp)', value: !!l.followCamera, enabled: true,
          title: 'The light rides the camera and owns its position — orbit to sweep it. The gizmo stands down while this is on.' },
        { kind: 'group', label: 'Emitter' },
        { kind: 'toggle', id: 'cube', label: 'Show emitter cube', value: !!l.cube, enabled: true,
          title: 'An unlit cube at the light, so you can see where it is (hidden while riding the camera).' },
        { kind: 'slider', id: 'cubeSize', label: 'cube size', value: l.cubeSize, min: 0.01, max: 0.4, step: 0.005, format: (v) => `${fix(2)(v)} m`, enabled: !!l.cube,
          title: l.cube ? 'Edge length of the emitter cube' : 'Show the emitter cube first' },
        remove,
      ]
    case 'hemisphere':
      return [
        name,
        { kind: 'group', label: 'Sky' },
        { kind: 'color', id: 'skyColor', label: 'color', value: l.skyColor },
        { kind: 'slider', id: 'skyIntensity', label: 'intensity', value: l.skyIntensity, min: 0, max: 3, step: 0.05, format: fix(2) },
        { kind: 'group', label: 'Ground' },
        { kind: 'color', id: 'groundColor', label: 'color', value: l.groundColor },
        { kind: 'slider', id: 'groundIntensity', label: 'intensity', value: l.groundIntensity, min: 0, max: 3, step: 0.05, format: fix(2) },
        { kind: 'group', label: 'Sky direction' },
        { kind: 'slider', id: 'elevation', label: 'elevation', value: l.elevation ?? 90, min: -90, max: 90, step: 1, format: deg,
          title: 'Where the SKY half comes from: 90 = straight up (the default), 0 = the horizon. The rotate gizmo tilts it too.' },
        { kind: 'slider', id: 'azimuth', label: 'azimuth', value: l.azimuth ?? 0, min: -180, max: 180, step: 1, format: deg },
        remove,
      ]
    default:
      return [name, remove]
  }
}

/** The Sky light row's controls — the `lighting` slice (IBL / key / ambient /
 *  light probe). A singleton: there is one environment map, so one row. */
function skyLightControls(lighting) {
  const g = lighting ?? {}
  const ibl = g.iblEnabled !== false
  const probe = g.lightProbeEnabled !== false
  return [
    { kind: 'toggle', id: 'iblEnabled', label: 'IBL (image-based lighting)', value: ibl, enabled: true,
      title: 'Light the avatar from the environment\'s baked cubemap. Off = the studio 3-light rig as a fallback.' },
    { kind: 'slider', id: 'envMapIntensity', label: 'IBL intensity', value: g.envMapIntensity ?? 1.5, min: 0, max: 5, step: 0.05, format: fix(2), enabled: ibl,
      title: ibl ? 'envMapIntensity on every avatar material' : 'Enable IBL first' },
    { kind: 'slider', id: 'keyLightIntensity', label: 'key light', value: g.keyLightIntensity ?? 1.2, min: 0, max: 4, step: 0.05, format: fix(2),
      title: 'An analytic directional layered on the IBL so normal maps catch light (pores, wrinkles, weave). Follows the sun\'s direction. 0 = pure IBL.' },
    { kind: 'group', label: 'Ambient' },
    { kind: 'slider', id: 'ambientIntensity', label: 'intensity', value: g.ambientIntensity ?? 0, min: 0, max: 2, step: 0.05, format: fix(2),
      title: 'Flat ambient fill, used until the IBL applies. Defaults to 0 — it washes out the IBL/shadow look.' },
    { kind: 'color', id: 'ambientColor', label: 'color', value: g.ambientColor ?? SCENE_WHITE },
    { kind: 'group', label: 'Light probe' },
    { kind: 'toggle', id: 'lightProbeEnabled', label: 'Light probe', value: probe, enabled: true,
      title: 'Baked-splat diffuse SH ambient bounce, driven by the avatar\'s position. Independent of IBL.' },
    { kind: 'slider', id: 'lightProbeIntensity', label: 'probe intensity', value: g.lightProbeIntensity ?? 1, min: 0, max: 2, step: 0.05, format: fix(2), enabled: probe,
      title: probe ? 'Multiplier on the probe\'s contribution' : 'Enable the light probe first' },
  ]
}

// ── Camera row controls (ENG-1190) ──────────────────────────────────────────
// The LiveTune panel's CAMERA section, moved onto the object it belongs to.
//
// Control ids are DOTTED PATHS into liveTuneStore's `camera` slice
// ('cage.hx'), and cameraControlPatch below turns one back into a
// setCameraFromPanel patch. The two functions sit together deliberately: that
// id vocabulary is the whole contract between the row that declares a control
// and the handler that performs it, so a rename landing on only one side is
// visible in the same screenful.
//
// Two shapes are not plain leaves:
//   - 'cage.gizmo' is the ephemeral "draw the cage in the viewport" view aid
//     (setCageGizmoVisible) — local view state, never persisted or shared, so
//     it yields a gizmo instruction rather than a patch.
//   - the x/y/z ids ('target.x', 'follow.z') each address ONE component of an
//     array the store merges as a leaf, so the patch rebuilds the whole triple.
//
// BLEND comes across too, in its own group and labelled: it is the duration of
// whatever camera move is in flight rather than part of the shared look, and
// dropping it would have deleted a control instead of moving one.
const CAM_AXIS = ['x', 'y', 'z']
const CAM_TARGET_VEC = [0, 1.5, 0]
const CAM_FOLLOW_OFFSET = [0, 1.6, 3]
const CAM_LEAF_GROUPS = ['cage', 'seeThrough', 'follow', 'blend']
const CAM_FOLLOW_OWNS = 'Follow is on and owns the camera position — the cage is not enforced while it runs.'
const triple = (v, fallback) => (Array.isArray(v) && v.length === 3 ? v : fallback)

function cameraControls(camera, { cageGizmo = false } = {}) {
  const c = camera ?? {}
  const cage = c.cage ?? {}
  const see = c.seeThrough ?? {}
  const follow = c.follow ?? {}
  const blend = c.blend ?? {}
  const mode = c.target?.mode ?? 'fixed'
  const vec = triple(c.target?.value, CAM_TARGET_VEC)
  const offset = triple(follow.offset, CAM_FOLLOW_OFFSET)
  // Follow owns the camera position while it runs, so the cage has nothing to
  // clamp. The panel greyed that block out; the row offers it disabled WITH the
  // reason, the way an unmounted sun's knobs are offered.
  const caged = !follow.enabled
  const cg = (ctl) => (caged ? ctl : { ...ctl, enabled: false, title: CAM_FOLLOW_OWNS })
  const num = (id, label, value, extra = {}) => ({ kind: 'number', id, label, value, step: 0.1, min: -50, ...extra })
  return [
    { kind: 'slider', id: 'fov', label: 'field of view', value: c.fov ?? 45, min: 20, max: 90, step: 1, format: deg,
      title: 'Vertical field of view. Lower flattens perspective (a portrait lens); higher exaggerates it.' },
    { kind: 'group', label: 'Look at' },
    { kind: 'choice', id: 'target.mode', label: 'target', value: mode,
      options: [['fixed', 'fixed'], ['head', 'head'], ['vector', 'vector']],
      title: 'fixed = whatever the orbit or preset looks at; head = track the avatar head; vector = a fixed world point.' },
    ...(mode === 'vector'
      ? CAM_AXIS.map((ax, i) => num(`target.${ax}`, ax, vec[i], { title: 'World point the camera looks at.' }))
      : []),
    { kind: 'group', label: 'Cage — box, offsettable from the avatar' },
    cg({ kind: 'toggle', id: 'cage.enabled', label: 'Cage enabled', value: !!cage.enabled,
      title: 'Keep the camera inside a box, so dollying out cannot pull it through splat walls.' }),
    cg(num('cage.hx', 'hx', cage.hx ?? 2.5, { min: 0.05, title: 'Half-extents: the box measures twice these, centred on the anchor.' })),
    cg(num('cage.hy', 'hy', cage.hy ?? 1.25, { min: 0.05 })),
    cg(num('cage.hz', 'hz', cage.hz ?? 2.5, { min: 0.05 })),
    cg(num('cage.ox', 'offset x', cage.ox ?? 0, {
      title: 'Moves the box away from the avatar — 0,0,0 centres it on the avatar. Deliberately not the orbit look-at point: panning (right-drag) does not drag the box with it.' })),
    cg(num('cage.oy', 'offset y', cage.oy ?? 0)),
    cg(num('cage.oz', 'offset z', cage.oz ?? 0)),
    cg(num('cage.rx', 'rot x', cage.rx ?? 0, { step: 0.05, min: -7,
      title: 'Tilts the box around its own centre (anchor + offset) — radians, XYZ Euler order. 0,0,0 keeps it axis-aligned.' })),
    cg(num('cage.ry', 'rot y', cage.ry ?? 0, { step: 0.05, min: -7 })),
    cg(num('cage.rz', 'rot z', cage.rz ?? 0, { step: 0.05, min: -7 })),
    cg({ kind: 'slider', id: 'cage.margin', label: 'margin', value: cage.margin ?? 0.5, min: 0, max: 1.5, step: 0.05, format: fix(2),
      title: 'How far inside the wall the camera is held.' }),
    { kind: 'toggle', id: 'cage.gizmo', label: 'Show cage in viewport', value: !!cageGizmo,
      title: 'A local view aid — drawn for you only, never saved and never part of the shared look.' },
    { kind: 'group', label: 'See-through' },
    { kind: 'toggle', id: 'seeThrough.enabled', label: 'See-through enabled', value: !!see.enabled,
      title: 'Fade environment splats between the camera and the head, so geometry does not occlude the face while orbiting.' },
    { kind: 'slider', id: 'seeThrough.radius', label: 'radius', value: see.radius ?? 0.6, min: 0, max: 2, step: 0.05, format: fix(2), enabled: !!see.enabled,
      title: see.enabled ? 'How wide a tunnel is faded, in metres.' : 'Enable see-through first' },
    { kind: 'slider', id: 'seeThrough.fade', label: 'fade', value: see.fade ?? 0.3, min: 0, max: 1, step: 0.05, format: fix(2), enabled: !!see.enabled,
      title: see.enabled ? 'How far the splats are faded down: 0 leaves them, 1 removes them.' : 'Enable see-through first' },
    { kind: 'toggle', id: 'seeThrough.auto', label: 'Auto', value: !!see.auto,
      title: 'Let the controller set the fade from camera geometry (near the head, or below it looking up) instead of holding it on. A proximity heuristic, not true per-splat occlusion.' },
    { kind: 'slider', id: 'seeThrough.autoNear', label: 'auto near', value: see.autoNear ?? 1.2, min: 0, max: 5, step: 0.1, format: fix(1), enabled: !!see.auto,
      title: see.auto ? 'How close to the head auto starts fading, in metres.' : 'Turn Auto on first' },
    { kind: 'toggle', id: 'seeThrough.autoBelow', label: 'Auto when below', value: !!see.autoBelow, enabled: !!see.auto,
      title: see.auto ? 'Also fade when the camera is under the head looking up.' : 'Turn Auto on first' },
    { kind: 'group', label: 'Follow' },
    { kind: 'toggle', id: 'follow.enabled', label: 'Follow enabled', value: !!follow.enabled,
      title: 'Keep the avatar framed as it moves. Overrides the preset camera pose, and the cage, while it runs.' },
    ...CAM_AXIS.map((ax, i) => num(`follow.${ax}`, `offset ${ax}`, offset[i], {
      step: 0.05, enabled: !!follow.enabled,
      title: follow.enabled ? 'Where the camera sits relative to the avatar.' : 'Turn Follow on first' })),
    { kind: 'choice', id: 'follow.space', label: 'space', value: follow.space ?? 'local', enabled: !!follow.enabled,
      options: [['local', 'local'], ['world', 'world']],
      title: 'local = the offset turns with the avatar, so the camera trails behind on turns. world = a fixed direction.' },
    { kind: 'slider', id: 'follow.damping', label: 'damping', value: follow.damping ?? 0.12, min: 0, max: 1, step: 0.01, format: fix(2), enabled: !!follow.enabled,
      title: 'Smooths the chase. 0 snaps.' },
    { kind: 'slider', id: 'follow.targetOffsetY', label: 'look y+', value: follow.targetOffsetY ?? 0, min: -2, max: 2, step: 0.05, format: fix(2), enabled: !!follow.enabled,
      title: 'Bias the look-at up or down, for screen framing.' },
    { kind: 'group', label: 'Blend — the move in flight' },
    { kind: 'slider', id: 'blend.fov', label: 'fov ms', value: blend.fov ?? 0, min: 0, max: 2000, step: 25, format: fix(0),
      title: 'How long a field-of-view change eases over. 0 snaps. Local: the duration of the CURRENT move, not part of the shared look.' },
    { kind: 'slider', id: 'blend.target', label: 'target ms', value: blend.target ?? 0, min: 0, max: 2000, step: 25, format: fix(0),
      title: 'How long a look-at change eases over. 0 snaps. Local, like fov ms above.' },
  ]
}

/** One camera control id and its new value, turned into what the store needs:
 *  `{ patch }` for setCameraFromPanel, `{ gizmo }` for the ephemeral cage view
 *  aid, or null for an id the slice has no home for — which the caller reports
 *  rather than swallowing, so a control nobody performs cannot sit there dead.
 *  Exported (unlike the builders above) because App.jsx's row-control handler
 *  is the other half of this contract. */
export function cameraControlPatch(controlId, value, camera) {
  const c = camera ?? {}
  if (controlId === 'cage.gizmo') return { gizmo: !!value }
  if (controlId === 'fov') return { patch: { fov: value } }
  const dot = controlId.indexOf('.')
  if (dot < 0) return null
  const group = controlId.slice(0, dot)
  const key = controlId.slice(dot + 1)
  const axis = CAM_AXIS.indexOf(key)
  if (group === 'target') {
    // Picking 'vector' seeds the point being edited, so the mode switch cannot
    // land on an undefined target; an axis edit implies vector mode.
    if (key === 'mode') {
      return value === 'vector'
        ? { patch: { target: { mode: 'vector', value: triple(c.target?.value, CAM_TARGET_VEC) } } }
        : { patch: { target: { mode: value } } }
    }
    if (axis < 0) return null
    const next = triple(c.target?.value, CAM_TARGET_VEC).slice()
    next[axis] = value
    return { patch: { target: { mode: 'vector', value: next } } }
  }
  if (group === 'follow' && axis >= 0) {
    const next = triple(c.follow?.offset, CAM_FOLLOW_OFFSET).slice()
    next[axis] = value
    return { patch: { follow: { offset: next } } }
  }
  // Everything else is a plain leaf on a sub-object, written only when the
  // slice already holds that key — so a typo cannot invent camera state (the
  // rule the Sky light row's handler follows).
  if (!CAM_LEAF_GROUPS.includes(group)) return null
  if (!c[group] || typeof c[group] !== 'object' || !(key in c[group])) return null
  return { patch: { [group]: { [key]: value } } }
}

/**
 * @param {object} input
 * @param {{id?:string,label?:string}|null} input.character
 * @param {Array<{id:string,label:string,isAvailable:()=>boolean,note?:string}>} input.adapters
 *   live transform adapters (getAdapters()).
 * @param {object|null} input.scene        THREE scene root (window.__AVATAR_SCENE__).
 * @param {'rig'|'splat'|'stream'|'missing'|'unknown'|null} input.rigStatus
 * @param {{nodes?:Array}|null} input.bodyGraph    graphState module ref
 * @param {{nodes?:Array}|null} input.faceGraph    faceGraphState module ref
 * @param {{nodes?:Array,edges?:Array}|null} input.eventGraph  eventGraphState module ref
 * @param {string|null} input.environmentName  active splat's display name
 * @param {boolean|null} [input.environmentBakeable]  isBakeableUrl(splatUrl) —
 *   whether the active environment's file is a .ply the bake service can
 *   rasterize. `false` disables the Build Navmesh button with the reason;
 *   null/undefined (unknown) leaves it enabled, so a caller that doesn't pass
 *   it keeps today's behaviour.
 * @param {'rig'|'editor'|null} [input.cameraOwner]  who drives the camera right
 *   now: the blueprint's authored rig, or the editor camera fallback. null when
 *   there is no scene to have a camera in.
 * @param {{location?:number[],rotation?:number[],target?:number[],fov?:number}|null}
 *   [input.cameraTransform]  cameraController.getTransform(), or null when the
 *   controller isn't bound to a camera yet.
 * @param {number|null} [input.cameraFov]  liveTuneStore's configured fov.
 * @param {string|null} [input.cameraTargetMode]  'fixed' | 'head' | 'vector'.
 * @param {object|null} [input.cameraTune]  liveTuneStore's whole `camera` slice,
 *   which the row turns into its controls (fov / target / cage / see-through /
 *   follow / blend). Null leaves the row read-only, as it was before ENG-1190.
 * @param {boolean} [input.cageGizmoVisible]  the ephemeral "draw the cage" view
 *   aid (liveTuneStore.getCageGizmoVisible) - local, never part of the look.
 * @param {'clean'|'dirty'|'saving'|'error'|'stale-backend'} [input.dirtyState]
 * @param {{dim?:number,subject?:string|null}|null} [input.rigBoard]  the loaded
 *   OGS bundle's own control space (rigBoardSource) — the SPLAT character's face
 *   rig, since it has no morph-target mesh to read one off. Passed in as a module
 *   ref, same as the graph states, so this stays pure for `node --test`.
 * @param {{available?:boolean,blend?:number,initial?:number,source?:string}|null}
 *   [input.rigConstrain]  the live ?rigconstrain weight (ogs/rig/rigConstrainBlend's
 *   module singleton) — how much of the trained clamp + subspace projection reaches
 *   the splat face rig. Drives the Face rig row's CONSTRAINT slider. Passed in, not
 *   read, so this stays pure for `node --test`.
 * @param {{available?:boolean,mouthWeight?:number,initial?:number,source?:string}|null}
 *   [input.avatarView]  avatarViewStore's state — the viewport's display of the rig
 *   avatar: `showJoints` (Armature row toggle) and `meshView` (Meshes row choice:
 *   lit | unlit | wireframe). View modes, not character properties.
 *   [input.liveFace]  the live-speech drive weights (ogs/rig/liveFaceControls) — the
 *   Face rig row's mouth-mix slider. Passed in, not read, so this stays pure.
 * @param {{state?:string,nodeCount?:number,size?:number}|null} [input.navmesh]
 *   window.__NAVMESH__ verbatim — the navigation row's dot + meta come from
 *   navmeshStatus.js's rules, not from this module's own reading of it. Passed in
 *   (not read here) so the function stays pure for `node --test`.
 * @param {{kind?:string,pct?:number}|null} [input.navmeshBake]  the navmesh row's
 *   bake state, so a running build reads as building rather than as missing.
 * @param {{navmesh?:boolean,voxels?:boolean,collision?:boolean,capsule?:boolean,
 *   walkPath?:boolean}|null} [input.navDebug]  navSettingsStore's `show` map —
 *   which navigation debug layers are drawn. Passed in, not read, so this stays
 *   pure for `node --test`.
 * @param {{agentRadius?:number,agentHeight?:number,navCell?:number}|null}
 *   [input.navParams]  the navmesh build parameters (the retired Build Navmesh
 *   panel's fields, now the NAVMESH row's own properties).
 * @param {{collisionMode?:string,collisionOnTop?:boolean}|null} [input.navStyle]
 *   how the collision mesh is drawn — the COLLISION row's properties.
 * @param {{state?:string,count?:number}|null} [input.voxels]  window.__VOXELS__.
 * @param {{state?:string}|null} [input.collision]  window.__COLLISION__.
 * @param {{groundEnabled?:boolean,groundColor?:string,groundSize?:number}|null}
 *   [input.backdrop]  liveTuneStore.getState().backdrop — drives the ground
 *   plane row's eye toggle. Passed in (not read here) so the function stays
 *   pure for `node --test`.
 * @param {Record<string,object>|null} [input.lights]  liveTuneStore.getState().lights
 *   verbatim (ENG-1190) — one row per entry under the Lighting parent, its
 *   `enabled` as the row's eye, its fields as inspector controls.
 * @param {object|null} [input.lighting]  liveTuneStore.getState().lighting — the
 *   Sky light row (IBL / key / ambient / light probe).
 * @param {number|null} [input.shadowCap]  the shadow-map ceiling THIS viewer's
 *   scalability tier imposes (shadowQuality.shadowCapForTier). Passed in, not
 *   read, so this stays pure; a light asking for more than the cap says so on
 *   its map control rather than looking like a dead switch.
 * @returns {Array<{id:string,label:string,glyph:string,depth:number,meta:string|null,
 *                  type:string,adapterId:string|null,parentId:string|null,
 *                  openSurface?:string,visible?:boolean,
 *                  status?:{tone:'ok'|'warn'|'error',title:string},
 *                  controls?:Array<{kind:'toggle'|'number'|'slider'|'choice'|'button'|'group'|'color'|'text',
 *                    id?:string,label:string,value?:any,enabled?:boolean,title?:string}>,
 *                  kv:Array<[string,string]>}>}
 */
export function buildOutlinerRows({
  character = null,
  adapters = [],
  scene = null,
  rigStatus = null,
  bodyGraph = null,
  faceGraph = null,
  eventGraph = null,
  environmentName = null,
  environmentBakeable = null,
  dirtyState = 'clean',
  cameraOwner = null,
  cameraTransform = null,
  cameraFov = null,
  navmesh = null,
  navmeshBake = null,
  navDebug = null,
  navParams = null,
  navStyle = null,
  voxels = null,
  collision = null,
  cameraTargetMode = null,
  cameraTune = null,
  cageGizmoVisible = false,
  backdrop = null,
  lights = null,
  lighting = null,
  shadowCap = null,
  rigBoard = null,
  rigConstrain = null,
  liveFace = null,
  avatarView = null,
} = {}) {
  if (!character) return []

  const rows = []
  const byLabel = (label) => adapters.find(a => a.label === label && a.isAvailable()) ?? null

  // ── Character root — the avatar adapter when one is live ──────────────────
  const avatarAdapter = byLabel('Avatar')
  const bodyNodes = bodyGraph?.nodes?.length ?? 0
  const faceNodes = faceGraph?.nodes?.length ?? 0
  const eventNodes = eventGraph?.nodes?.length ?? 0
  const blueprints = [bodyNodes && 'body', faceNodes && 'face', eventNodes && 'events']
    .filter(Boolean).join(' · ')

  rows.push({
    id: 'character',
    label: character.label ?? character.id ?? 'Character',
    glyph: '◈',
    depth: 0,
    meta: 'CHARACTER',
    type: 'CHARACTER',
    adapterId: avatarAdapter?.id ?? null,
    parentId: null,
    // NO rigPreview here. The rig board belongs to the Face rig row, which is
    // where you go looking for face controls and which now exists for every
    // splat character (see the Face rig section below) — so the root's copy was
    // a second button for the same panel one row above the row that owns it.
    // It was on the root originally (ENG-946) only because the Face rig row was
    // read off morph-target meshes a splat character hasn't got. (ENG-1042)
    kv: [
      ['id', character.id ?? '—'],
      ...(rigStatus && rigStatus !== 'unknown' ? [['pipeline', rigStatus.toUpperCase()]] : []),
      ...(blueprints ? [['blueprints', blueprints]] : []),
      ...(environmentName ? [['environment', environmentName]] : []),
      ['unsaved', dirtyState === 'clean' ? 'no' : 'yes'],
    ],
  })

  // ── Skeleton ───────────────────────────────────────────────────────────────
  const bones = countBones(scene)
  if (bones > 0) {
    rows.push({
      id: 'armature',
      label: 'Armature',
      glyph: '⌒',
      depth: 1,
      meta: String(bones),
      type: 'SKELETON',
      adapterId: null,
      parentId: 'character',
      kv: [['joints', fmt(bones)]],
      // Draw the skeleton over the meshes (THREE.SkeletonHelper). A view mode of
      // the viewport, so it lives on the row that IS the skeleton, resets with
      // the page, and is never saved with the character.
      controls: [
        {
          kind: 'toggle', id: 'show-joints', label: 'Display joints', enabled: true,
          value: !!avatarView?.showJoints,
          title: 'Draw every bone of the rig as a line over the meshes, on top of everything. Viewport only; not saved.',
        },
      ],
    })
  }

  // ── Meshes ─────────────────────────────────────────────────────────────────
  const meshes = scene ? collectMeshes(scene) : []
  if (meshes.length) {
    const totalTris = meshes.reduce((s, m) => s + (m.tris ?? 0), 0)
    rows.push({
      id: 'meshes',
      label: 'Meshes',
      glyph: '▤',
      depth: 1,
      meta: String(meshes.length),
      type: 'GROUP',
      adapterId: null,
      parentId: 'character',
      kv: [
        ['children', String(meshes.length)],
        ...(totalTris ? [['tris', fmt(totalTris)]] : []),
      ],
      // How the meshes render, for the whole group at once: the authored
      // materials, base colour only (Unreal's Unlit), or wireframe. Applied by
      // AvatarViewGizmos through material flags, so nothing is swapped or lost.
      controls: [
        {
          kind: 'choice', id: 'mesh-view', label: 'view',
          value: avatarView?.meshView ?? 'lit',
          options: [['lit', 'Lit'], ['unlit', 'Unlit'], ['wireframe', 'Wireframe']],
          title: 'Lit: the materials as authored. Unlit: each material\'s base colour with no lighting, to judge textures. Wireframe: the geometry. Viewport only; not saved.',
        },
      ],
    })
    for (const m of meshes) {
      rows.push({
        id: `mesh:${m.name}`,
        label: m.name,
        glyph: '◐',
        depth: 2,
        meta: 'MESH',
        type: 'MESH',
        adapterId: null,
        parentId: 'meshes',
        kv: [
          ...(m.tris != null ? [['tris', fmt(m.tris)]] : []),
          ...(m.morphs ? [['morph targets', String(m.morphs)]] : []),
          ['visible', m.visible ? 'yes' : 'no'],
        ],
      })
    }
  }

  // ── Face rig ───────────────────────────────────────────────────────────────
  // TWO sources, and which one is real is decided by the pipeline — this row is
  // where you go looking for face controls, so it must exist for both (ENG-1042).
  //
  //   GLB character  — the face IS the head mesh's morph targets. Only claimed
  //                    when some mesh actually carries them.
  //   SPLAT character — there is no such mesh: `scene` here is
  //                    window.__AVATAR_SCENE__, which ONLY AvatarModel sets and
  //                    which never mounts in splat mode. Reading the row off
  //                    morph targets is why a splat character had no Face rig row
  //                    at all. Its face is the bundle's own control space
  //                    (rigBoardSource), so that is what the row reports.
  const morphed = meshes.filter(m => m.morphs > 0)
  const boardDim = Number(rigBoard?.dim) || 0
  // The face rig's own PROPERTIES, in the inspector rather than in a floating panel,
  // for the same reason the navmesh build params belong to the NAVMESH row: a
  // feature's properties live with the feature, and this is the row you already open
  // to ask what the face rig is.
  //
  // Two groups, and the split is the point — they are different jobs on the same
  // frame. `Live speech` is a DRIVE (the trained mouth mix rewrites the mouth block);
  // `Constraint` BOUNDS whatever wrote it (the trained-domain clamp + the subspace
  // projection). They sit together because they interact: at the shipped 0.65 the mix
  // has already pulled the mouth most of the way onto the trained manifold before
  // either constraint runs, which is why the constraint moves a talking face far less
  // than a static extreme pose.
  //
  // Each half is offered on its OWN terms — a bundle can ship a mouth drive with no
  // armed constraint weight, or the reverse — and neither appears on a GLB character,
  // where none of this path runs. A slider that silently does nothing is worse than
  // no slider. Each reset appears only once the value differs from what this
  // character loaded with, since that is the only state it has a job in.
  const mouthControls = () => {
    if (!liveFace?.available) return []
    const m = Number(liveFace.mouthWeight ?? 0.65)
    const init = Number(liveFace.initial ?? 0.65)
    return [
      { kind: 'group', label: 'Live speech' },
      {
        kind: 'slider', id: 'live-mouth', label: 'trained mouth mix',
        value: m, min: 0, max: 1, step: 0.05, format: (v) => v.toFixed(2),
        title: "How far the live A2F mouth block is pulled toward this bundle's trained "
          + 'closed/open references. The A2F jaw still picks the aperture; the references '
          + 'decide what a mouth at that aperture looks like. 0 = the raw rest+delta mouth.',
        note: m <= 0 ? 'OFF — the raw A2F mouth (rest + delta), where the live lip artifacts come from.'
          : m >= 1 ? 'The mouth block is REPLACED by the trained interpolation; A2F sets only the aperture.'
            : `${Math.round(m * 100)}% toward the trained references; A2F keeps the aperture.`,
      },
      ...(m !== init ? [{
        kind: 'button', id: 'live-mouth-reset', label: `reset to ${init.toFixed(2)}`,
        title: liveFace.source === 'panel'
          ? 'Back to the value this character loaded with'
          : 'Back to the URL / default value',
      }] : []),
    ]
  }
  const constraintControls = () => {
    if (!rigConstrain?.available) return []
    const w = Number(rigConstrain.blend ?? 1)
    const initial = Number(rigConstrain.initial ?? 1)
    return [
      { kind: 'group', label: 'Constraint' },
      {
        kind: 'slider', id: 'rig-constrain', label: 'trained clamp + PCA',
        value: w, min: 0, max: 1, step: 0.05, format: (v) => v.toFixed(2),
        title: 'How much of the trained-domain clamp and the trained subspace projection reaches '
          + 'the rig. 1 = the shipped face; 0 = the raw ARKit-composed rig (?rigclamp=none&rigpca=off).',
        // What it is doing RIGHT NOW, when there is a twin to compare against.
        // "The slider does nothing" and "this pose cannot show what it is doing" look
        // identical on screen and have opposite fixes.
        live: rigConstrain.delta
          ? `now moving ${rigConstrain.delta.moved} controls, worst ${rigConstrain.delta.max.toFixed(3)}`
          : null,
        note: w >= 1 ? 'Trained clamp + subspace projection fully applied — the shipped face.'
          : w <= 0 ? 'NEITHER constraint — the raw ARKit rig. Expect the decoders to over-respond.'
            : `${Math.round(w * 100)}% of the constraint — blended between the constrained rig and the raw one.`,
      },
      ...(w !== initial ? [{
        kind: 'button', id: 'rig-constrain-reset', label: `reset to ${initial.toFixed(2)}`,
        title: rigConstrain.source === 'panel'
          ? 'Back to the value this character loaded with'
          : 'Back to the URL / default value',
      }] : []),
    ]
  }
  const faceRigControls = () => {
    const all = [...mouthControls(), ...constraintControls()]
    return all.length ? all : undefined
  }

  if (morphed.length) {
    rows.push({
      id: 'face-rig',
      label: 'Face rig',
      glyph: SURFACE_GLYPH.face,
      depth: 1,
      meta: String(Math.max(...morphed.map(m => m.morphs))),
      type: 'FACE',
      adapterId: null,
      parentId: 'character',
      rigPreview: rigStatus === 'splat',
      controls: faceRigControls(),
      kv: [
        ['shape targets', String(Math.max(...morphed.map(m => m.morphs)))],
        ['carried by', morphed.map(m => m.name).join(', ')],
        ...(faceNodes ? [['face graph nodes', String(faceNodes)]] : []),
      ],
    })
  } else if (rigStatus === 'splat') {
    // The row exists on the strength of the PIPELINE, not the board: the panel
    // always offers ARKit-52 for a splat character, so the controls are reachable
    // before the bundle publishes its own control space. The board's numbers fill
    // in when it registers — an unpublished board contributes no count rather
    // than a plausible one.
    rows.push({
      id: 'face-rig',
      label: 'Face rig',
      glyph: SURFACE_GLYPH.face,
      depth: 1,
      meta: boardDim ? String(boardDim) : null,
      type: 'FACE',
      adapterId: null,
      parentId: 'character',
      rigPreview: true,
      controls: faceRigControls(),
      kv: [
        ...(boardDim ? [['rig controls', String(boardDim)]] : []),
        ...(rigBoard?.subject ? [['subject', String(rigBoard.subject)]] : []),
        ...(faceNodes ? [['face graph nodes', String(faceNodes)]] : []),
      ],
    })
  }

  // ── Blueprints — the three graphs, each row opening its full-viewport
  //    editor (double-click, or the inspector's Open button). Counts are the
  //    live module refs, so an empty graph reads 0 rather than pretending. ──
  const graphRows = [
    { id: 'graph-body', label: SURFACE_LABELS.body, surface: 'body', count: bodyNodes, extra: bodyGraph },
    { id: 'graph-face', label: SURFACE_LABELS.face, surface: 'face', count: faceNodes, extra: faceGraph },
    { id: 'graph-events', label: SURFACE_LABELS.events, surface: 'events', count: eventNodes, extra: eventGraph },
  ]
  rows.push({
    id: 'blueprints',
    label: 'Blueprints',
    glyph: SURFACE_GLYPH.events,
    depth: 1,
    meta: String(graphRows.filter(g => g.count > 0).length),
    type: 'GRAPH',
    adapterId: null,
    parentId: 'character',
    kv: graphRows.map(g => [g.label.toLowerCase(), `${g.count} nodes`]),
  })
  for (const g of graphRows) {
    rows.push({
      id: g.id,
      label: g.label,
      glyph: '⌁',
      depth: 2,
      meta: String(g.count),
      type: 'GRAPH',
      adapterId: null,
      parentId: 'blueprints',
      openSurface: g.surface,
      kv: [
        ['nodes', String(g.count)],
        ...(g.extra?.edges ? [['edges', String(g.extra.edges.length)]] : []),
      ],
    })
  }

  // ── Camera ─────────────────────────────────────────────────────────────────
  // The camera has no transform adapter because it is not a scene object you
  // drag: in live mode the BLUEPRINT drives it (an authored rig — the
  // `cam_group_orbit` graphGroup in the Event Graph), and with no rig authored
  // the editor's OrbitControls takes over as a LABELLED fallback (Scene.jsx,
  // cameraRigCompat). That distinction is invisible everywhere else in the
  // shell, which is exactly why it earns a row: the row's meta IS the answer to
  // "who is moving my camera".
  //
  // Double-click opens the Event Graph, because that is where a rig is authored
  // — whether you are inspecting the one you have or going to add one.
  if (cameraOwner) {
    const rigDriven = cameraOwner === 'rig'
    const fov = Number.isFinite(cameraTransform?.fov) ? cameraTransform.fov
      : Number.isFinite(cameraFov) ? cameraFov : null
    rows.push({
      id: 'camera',
      label: 'Camera',
      glyph: '▣',
      depth: 0,
      meta: rigDriven ? 'RIG' : 'EDITOR',
      type: 'CAMERA',
      adapterId: null,
      parentId: null,
      openSurface: 'events',
      openLabel: 'Open Event graph',
      // The camera's OWN settings, which lived in the LiveTune panel's CAMERA
      // section until ENG-1190. It still has no transform adapter (see above) -
      // these are properties of the camera, not a gizmo you drag.
      ...(cameraTune ? { controls: cameraControls(cameraTune, { cageGizmo: cageGizmoVisible }) } : {}),
      kv: [
        ['driven by', rigDriven ? 'blueprint rig' : 'editor camera'],
        // The diagnostic the notice chip carries, restated where you go looking
        // for the camera rather than only where it happens to overlay.
        ...(rigDriven ? [] : [['rig', 'none authored — add one in the Event Graph']]),
        ...(fov != null ? [['fov', `${Math.round(fov)}°`]] : []),
        ...(cameraTargetMode ? [['target', cameraTargetMode]] : []),
        // Position/rotation only when the controller is actually bound to a
        // camera; an unbound controller reports nothing rather than zeros.
        ...(cameraTransform?.location
          ? [['location', cameraTransform.location.map(v => v.toFixed(2)).join('  ')]] : []),
        ...(cameraTransform?.target
          ? [['look at', cameraTransform.target.map(v => v.toFixed(2)).join('  ')]] : []),
      ],
    })
  }

  // ── Environment ────────────────────────────────────────────────────────────
  const envAdapter = byLabel('Environment')
  if (envAdapter) {
    rows.push({
      id: 'environment',
      label: environmentName ?? 'Environment',
      glyph: '⬢',
      depth: 0,
      meta: 'GSPLAT',
      type: 'GSPLAT',
      adapterId: envAdapter.id,
      parentId: null,
      kv: [
        ...(environmentName ? [['source', environmentName]] : []),
        ...(envAdapter.note ? [['note', envAdapter.note]] : []),
      ],
    })
  }

  // ── Lighting (ENG-1190: lights are objects) ───────────────────────────────
  // A parent row that adds lights, a Sky light row for the environment map's
  // knobs, and one row per light in liveTuneStore.lights. Every light row is
  // REAL — it exists because the store holds an entry, and the eye is the
  // entry's own `enabled`. Every light rides its transform adapter (a gizmo:
  // move for point, move + rotate for spot and sun, rotate for hemisphere) —
  // the one exception is a point light riding the camera, whose adapter
  // reports unavailable.
  const lightEntries = lights && typeof lights === 'object' && !Array.isArray(lights)
    ? Object.entries(lights).filter(([, l]) => l && typeof l === 'object')
    : []
  const lightsOn = lightEntries.filter(([, l]) => l.enabled).length
  rows.push({
    id: 'lighting',
    label: 'Lighting',
    glyph: '☀',
    depth: 0,
    meta: `${lightsOn} ON`,
    type: 'LIGHT',
    adapterId: null,
    parentId: null,
    controls: [
      { kind: 'group', label: 'Add light' },
      ...LIGHT_TYPES.map(t => ({
        kind: 'button', id: `add-${t}`, label: `Add ${LIGHT_TYPE_LABEL[t]}`,
        title: t === 'directional' ? 'A shadow-casting sun. Mounts while an environment is loaded.'
          : t === 'spot' ? 'A cone on an orbit rig, aimed at the character, with its own shadow map.'
            : t === 'point' ? 'A movable point light with an emitter cube — walk it past the subject and watch the highlight move.'
              : 'Sky / ground ambient fill.',
      })),
    ],
    kv: [
      ['lights', String(lightEntries.length)],
      ['on', String(lightsOn)],
    ],
  })
  rows.push({
    id: 'skylight',
    label: 'Sky light',
    glyph: '◌',
    depth: 1,
    meta: 'IBL',
    type: 'LIGHT',
    adapterId: null,
    parentId: 'lighting',
    controls: skyLightControls(lighting),
    kv: [
      ['ibl', lighting?.iblEnabled === false ? 'off' : 'on'],
      ...(environmentName ? [['from', environmentName]] : [['from', 'no environment — studio rig']]),
    ],
  })
  for (const [id, l] of lightEntries) {
    const adapter = adapters.find(a => a.id === id && a.isAvailable()) ?? null
    // A directional only mounts with an environment (its VSM pass is the most
    // expensive thing in a software-rendered frame, and the studio rig's em_key
    // already casts there). Its knobs are offered-and-disabled with that reason
    // rather than hidden or silently inert.
    const inert = l.type === 'directional' && !environmentName
      ? 'Mounts once an environment is loaded — the studio rig lights the empty stage.'
      : null
    rows.push({
      id: `light:${id}`,
      label: l.name || LIGHT_TYPE_LABEL[l.type] || id,
      glyph: LIGHT_GLYPH[l.type] ?? '✦',
      depth: 1,
      meta: LIGHT_TYPE_META[l.type] ?? 'LIGHT',
      type: 'LIGHT',
      adapterId: adapter?.id ?? null,
      parentId: 'lighting',
      visible: !!l.enabled,
      controls: lightControls(l, { inert, shadowCap }),
      kv: [
        ['type', LIGHT_TYPE_LABEL[l.type] ?? String(l.type)],
        ['enabled', l.enabled ? 'yes' : 'no'],
        ...(inert ? [['mounts', 'with an environment']] : []),
        ...(adapter?.note ? [['note', adapter.note]] : []),
      ],
    })
  }

  // ── Navmesh (navigation) ───────────────────────────────────────────────────
  // Like the ground plane, NOT a scene object: it's the navigation graph the
  // runtime paths on (window.__NAVMESH__, published by NavMeshVolume). It earns a
  // row because "can the character walk here?" is an outliner question, and
  // because the answer was previously invisible — a missing bake surfaced only as
  // a console warning, so a right-click "Walk here" that refused had nothing to
  // point at. The dot is the answer (green = navigable now), the meta says which
  // KIND (baked / studio floor), and selecting the row draws the graph.
  const nmStatus = navmeshRowStatus(navmesh, navmeshBake)
  // The sibling artifacts' status feeds the enabled/reason of their controls
  // below — they have no row of their own any more.
  const vxStatus = artifactRowStatus(voxels, 'Voxels', 'voxel grid')
  const clStatus = artifactRowStatus(collision, 'Collision', 'collision mesh')
  rows.push({
    id: 'navmesh',
    label: nmStatus.label,
    glyph: '⬡',
    depth: 0,
    meta: nmStatus.meta,
    type: 'NAVMESH',
    adapterId: null,
    parentId: null,
    status: { tone: nmStatus.tone, title: nmStatus.detail },
    // The navmesh's own properties — what the retired "Build Navmesh" panel
    // held. An unavailable control is disabled WITH its reason, never hidden.
    controls: [
      {
        kind: 'toggle', id: 'show-navmesh', label: 'Show navmesh grid',
        value: !!navDebug?.navmesh, enabled: nmStatus.built,
        title: nmStatus.built
          ? 'Draw the walkable graph in the viewport'
          : 'Nothing to draw yet — ' + nmStatus.detail,
      },
      {
        kind: 'toggle', id: 'show-capsule', label: 'Show agent capsule',
        value: !!navDebug?.capsule, enabled: true,
        title: 'The character capsule the build carves around (radius + height below)',
      },
      {
        // The right-click "Walk here" preview: route line + destination ring.
        kind: 'toggle', id: 'show-walk-path', label: 'Show walk path',
        value: navDebug?.walkPath !== false, enabled: true,
        title: 'Draw the planned route and destination when walking somewhere',
      },
      { kind: 'group', label: 'Character capsule' },
      {
        kind: 'number', id: 'agent-radius', label: 'radius', unit: 'm',
        value: navParams?.agentRadius ?? 0.25, step: 0.05, min: 0.05,
        title: 'Walkable erosion — how close to a wall the character may stand',
      },
      {
        kind: 'number', id: 'agent-height', label: 'height', unit: 'm',
        value: navParams?.agentHeight ?? 1.7, step: 0.1, min: 0.5,
        title: 'Standing clearance — how low a ceiling still counts as walkable',
      },
      {
        kind: 'number', id: 'nav-cell', label: 'cell', unit: 'm',
        value: navParams?.navCell ?? 0.2, step: 0.05, min: 0.05,
        title: 'Navmesh grid resolution',
      },
      {
        // Building needs an environment to bake FOR: the studio floor grid is
        // synthesised on the client, so there is nothing to send. And it needs
        // a .ply SOURCE: the bake rasterizer is INRIA .ply-only. A compressed
        // row (.spz) whose folder retains the source .ply (splat_ply, ENG-1070)
        // is still bakeable — the server bakes the sibling — so `false` here
        // means the server lists NO .ply at all, and the button disables with
        // that reason instead of relaying the bake's mid-parse 500.
        kind: 'button', id: 'build-navmesh', label: 'Build Navmesh',
        enabled: !!environmentName && environmentBakeable !== false, primary: true,
        title: !environmentName
          ? 'Load an environment first — the studio floor grid needs no bake'
          : environmentBakeable === false
            ? 'This environment has no .ply source — the bake can only rasterize .ply, and the server lists no .ply for it. Upload the .ply to build a navmesh.'
            : 'Bake the navmesh for ' + environmentName,
      },
      // The navmesh bake's SIBLING artifacts. They had their own outliner rows
      // briefly and Andres cut them: two more permanent rows for layers people
      // flip while debugging a bake is clutter in a list you read constantly.
      // Their controls stay — they are the only UI for these layers now that the
      // Build Navmesh window is gone — and they live here because they come from
      // the same bake. Each is disabled with its own reason when its artifact
      // isn't there, which is what the rows were carrying.
      ...(environmentName ? [
        { kind: 'group', label: 'Voxels' },
        {
          kind: 'toggle', id: 'show-voxels', label: 'Show voxels',
          value: !!navDebug?.voxels, enabled: vxStatus.built,
          title: vxStatus.built ? 'Draw the voxel-surface cubes' : 'Nothing to draw yet — ' + vxStatus.detail,
        },
        { kind: 'group', label: 'Collision mesh' },
        {
          kind: 'toggle', id: 'show-collision', label: 'Show collision mesh',
          value: !!navDebug?.collision, enabled: clStatus.built,
          title: clStatus.built ? 'Draw the baked collision surface' : 'Nothing to draw yet — ' + clStatus.detail,
        },
        {
          kind: 'choice', id: 'collision-mode', label: 'style',
          value: navStyle?.collisionMode ?? 'shaded',
          options: [['shaded', 'shaded'], ['wireframe', 'wireframe']],
          enabled: clStatus.built,
          title: 'Shaded reads the near surface; wireframe reads the edges',
        },
        {
          kind: 'toggle', id: 'collision-on-top', label: 'Always on top',
          value: !!navStyle?.collisionOnTop, enabled: clStatus.built,
          title: 'Ignore depth so the splat cannot occlude it',
        },
      ] : []),
    ],
    kv: [
      ['built', nmStatus.built ? 'yes' : 'no'],
      ...(Number.isFinite(navmesh?.nodeCount) ? [['nodes', String(navmesh.nodeCount)]] : []),
      ...(Number.isFinite(navmesh?.size) ? [['size', `${navmesh.size} m`]] : []),
      ...(Number.isFinite(navmesh?.floorY) ? [['floor y', String(navmesh.floorY)]] : []),
    ],
  })

  // ── Ground plane (backdrop) ────────────────────────────────────────────────
  // NOT a scene object: it's liveTuneStore.backdrop.groundEnabled, rendered
  // conditionally by Scene.jsx. It still earns a row because "why is there a
  // floor / why is there NO floor" is an outliner question — answered with an
  // eye toggle (`visible`, the store value verbatim) instead of a gizmo. It
  // rides along with any character, since the stage exists whether or not the
  // plane is currently shown.
  rows.push({
    id: 'groundPlane',
    label: 'Ground Plane',
    glyph: '▬',
    depth: 0,
    meta: 'PLANE',
    type: 'PLANE',
    adapterId: null,
    parentId: null,
    visible: !!backdrop?.groundEnabled,
    kv: [
      ['visible', backdrop?.groundEnabled ? 'yes' : 'no'],
      ...(backdrop?.groundColor ? [['color', String(backdrop.groundColor)]] : []),
      ...(Number.isFinite(backdrop?.groundSize) ? [['size', `${backdrop.groundSize} m`]] : []),
    ],
  })

  return rows
}

export default buildOutlinerRows
